// FIX-CCS-OUTBOX (spec §7.3) — one send of one outbox item to upload-photo, with a size-based time limit.
//
// The session token is read at send time by the caller and passed in; it is never stored in the item.
// The outcome is classified for the runner:
//   ok               200 success (fresh or duplicate:true)          → confirm the row, then delete the item
//   network          fetch rejected (no signal, DNS, CORS abort)    → back off, keep
//   timeout          the size-based limit ran out                   → treated as network (back off, keep)
//   server_error     5xx or an unreadable reply                     → back off, keep
//   auth             401 (session_missing / session_invalid)        → stop the run, keep everything, ask for the PIN (R6)
//   offline_disabled 403 offline_disabled (kill switch off, R16)    → keep, show the state, never drop
//   refused          any other 4xx                                  → failed state with Retry; never silently dropped

import { captureClaimFields, type CaptureClaim } from '@/features/capture/lib/captureClaims';
import { recordServerNow } from '@/shared/lib/serverClock';
import { sendTimeoutMs } from './limits';
import type { OutboxItem } from './types';

export type SendOutcome =
  | { kind: 'ok'; duplicate: boolean; status: number; body: Record<string, unknown> }
  | { kind: 'network'; detail: string }
  | { kind: 'timeout'; detail: string }
  | { kind: 'server_error'; status: number; detail: string }
  | { kind: 'auth'; status: number; detail: string }
  | { kind: 'offline_disabled'; status: number; detail: string }
  | { kind: 'refused'; status: number; detail: string };

export interface SendConfig {
  supabaseUrl: string;
  anonKey: string;
  sessionToken: string;
  /** 'live' only for the attempt made at save; every later send is 'queued'. */
  uploadSource: 'live' | 'queued';
  fetchImpl?: typeof fetch;
  nowMs?: number;
  /** This is the send made at Save (no client_queued_at). */
  atSave?: boolean;
  /** Override the size-based limit (tests only). */
  timeoutMs?: number;
}

/** The multipart form for one item — the same fields the live camera path sends (B1/C1), from the item's frozen state. */
export function buildUploadForm(item: OutboxItem, uploadSource: 'live' | 'queued', nowMs: number = Date.now(), atSave = false): FormData {
  const fd = new FormData();
  fd.append('file', item.original, `${item.photo_id}.${item.original_ext}`);
  if (item.preview) fd.append('thumbnail', item.preview, `${item.photo_id}_thumb.jpg`);
  fd.append('upload_transform', item.upload_transform);
  fd.append('upload_source', uploadSource);
  fd.append('client_chain', 'server');

  const claim: CaptureClaim = {
    picked_at_client: item.claims.picked_at_client,
    captured_at_client: item.claims.captured_at_client,
    offline_at_pick: item.claims.offline_at_pick,
    client_offset_before_s: item.claims.client_offset_before_s,
  };
  const fields = captureClaimFields(claim, {
    sendFailedBefore: item.send_failed_before,
    contextAsOf: item.context_as_of,
    nowMs,
    frozenSeq: { client_install_id: item.claims.client_install_id, client_seq: item.claims.client_seq },
    // Every send except the save-time one carries when it was queued (also a 'live' send under the switch-off rule).
    clientQueuedAt: atSave ? undefined : item.queued_at,
  });
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);

  if (item.context.cycle_id) fd.append('farmerCycleId', item.context.cycle_id);
  if (item.context.crop_id) fd.append('cropId', item.context.crop_id);
  if (item.context.stage_id) fd.append('stageId', item.context.stage_id);
  if (item.context.farm_id) fd.append('farmId', item.context.farm_id);
  fd.append('photoId', item.photo_id);
  fd.append('category', item.category);
  fd.append('fileSize', String(item.size_bytes));

  // R4: the fix taken at pick, with its age, is the only location sent — never a fix taken at sync.
  const pre: Record<string, unknown> =
    item.precapture && typeof item.precapture === 'object' && !Array.isArray(item.precapture) ? { ...(item.precapture as Record<string, unknown>) } : {};
  pre.outbox = {
    location_state: item.location_state ?? null,
    gps_fix_at: item.gps?.fix_at ?? null,
    gps_age_s_at_pick: item.gps?.age_s_at_pick ?? null,
    queued_at: item.queued_at,
    attempts: item.attempts,
  };
  fd.append('precapture_metadata', JSON.stringify(pre));
  // FIX-CCS-G-GPS: the Location Bundle, verbatim, as written at the shutter (or once when the hold resolved).
  if (item.location_bundle) fd.append('location_bundle', JSON.stringify(item.location_bundle));
  // Items saved by builds before G-GPS carry only the loose pick-time fix (accepted before cutover, legacy_*).
  else if (item.location_state === undefined && item.gps) {
    fd.append('gpsLat', String(item.gps.lat));
    fd.append('gpsLng', String(item.gps.lng));
    if (item.gps.accuracy_m !== null) fd.append('gpsAccuracy', String(item.gps.accuracy_m));
  }
  return fd;
}

export async function sendOutboxItem(item: OutboxItem, cfg: SendConfig): Promise<SendOutcome> {
  const nowMs = cfg.nowMs ?? Date.now();
  const limit = cfg.timeoutMs ?? sendTimeoutMs(item.size_bytes);
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, limit);
  const doFetch = cfg.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await doFetch(`${cfg.supabaseUrl}/functions/v1/upload-photo`, {
      method: 'POST',
      headers: {
        'x-session-token': cfg.sessionToken,
        apikey: cfg.anonKey,
        Authorization: `Bearer ${cfg.anonKey}`,
      },
      body: buildUploadForm(item, cfg.uploadSource, nowMs, cfg.atSave === true),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (timedOut) return { kind: 'timeout', detail: `no reply within ${Math.round(limit / 1000)} s` };
    return { kind: 'network', detail: e instanceof Error ? e.message : String(e) };
  }

  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    clearTimeout(timer);
    if (timedOut) return { kind: 'timeout', detail: `reply cut after ${Math.round(limit / 1000)} s` };
    if (response.status >= 500 || response.ok) return { kind: 'server_error', status: response.status, detail: 'unreadable reply' };
    body = {};
  }
  clearTimeout(timer);
  recordServerNow(body.server_now, 'upload-photo');

  const code = typeof body.reason_code === 'string' ? body.reason_code : String(response.status);
  if (response.ok && body.success === true) {
    return { kind: 'ok', duplicate: body.duplicate === true, status: response.status, body };
  }
  if (response.status === 401) return { kind: 'auth', status: 401, detail: code };
  if (response.status === 403 && code === 'offline_disabled') return { kind: 'offline_disabled', status: 403, detail: code };
  if (response.status >= 500 || response.ok) return { kind: 'server_error', status: response.status, detail: code };
  // 408/429 are transient by definition: back off instead of failing the item.
  if (response.status === 408 || response.status === 429) return { kind: 'server_error', status: response.status, detail: code };
  return { kind: 'refused', status: response.status, detail: code };
}
